import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as moment from 'moment';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

import { AZURE_CA_URLS } from '../../../app/shared/enums/HttpStatusCodes';
import { ISuccesfull, Users } from '../../shared/models/ApplicationUser';
import { AUTENTIFICACION_ENDPOINTS, GESTION_USUARIOS_ENDPOINTS } from '../../shared/models/Constants';
import { FcmTokenService } from '../../shared/services/fcm/fcm-token.service';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
    ChangePass,
    Counters,
    CountersSave,
    errorChangePass,
    errorCounters,
    errorCountersSave,
    errorLoginTelPass,
    errorPhoneVaidationPass,
    errorPreRegisterUser,
    errorRegisterUser,
    errorSendSmsPhone,
    errorUpdatedUserData,
    loginTelPass,
    phoneVaidationPass,
    preRegisterUser,
    registerUser,
    SendSmsPhone,
    successChangePass,
    successCounters,
    successCountersSave,
    successLoginTelPass,
    successPhoneVaidationPass,
    successPreRegisterUser,
    successRegisterUser,
    successSendSmsPhone,
    successUpdatedUserData,
    updatedUserData,
} from '../actions';
import { AppSettings } from './../../app.settings';
import { HandleErrorService } from './../../shared/interceptors/handle-error.service';
import { ICustomResponseResult } from './../../shared/models/request.model';
import {
    ILoginRequest,
    ILoginTelPass,
    IRefreshAuthTokenResponse,
    TenderoLogin,
} from './../../shared/models/TenderoLogin.interface';
import { EncryptionService } from './../../shared/services/encryption/encryption.service';
import { StorageData } from './../../shared/services/utilities/storage-data.class';
import { UtilitiesService } from './../../shared/services/utilities/utilities.service';
import {
    errorRefreshAuthToken,
    errorSendCodeVerify,
    RefreshAuthToken,
    SendCodeVerify,
    successRefreshAuthToken,
    successSendCodeVerify,
} from './../actions/authentication.actions';

@Injectable()
export class AuthenticationEffets {
    constructor(
        private handleErrorS: HandleErrorService
        , private encryptionS: EncryptionService
        , private utilitiesS: UtilitiesService
        , private httpS: SharedServiceService
        , private actions$: Actions
        , private readonly fcmTokenS: FcmTokenService
    ) { }

    /**
     * * Baja los datos actualizados del usuario
     */
    public updatedUserData$ = createEffect(
        () => this.actions$.pipe(
            ofType(updatedUserData),
            switchMap(actions => from(this.fcmTokenS.getToken())
                .pipe(
                    map(token => ({ ...actions, TokenFirebase: token, offset: (moment().utcOffset() / 60) })),
                )
            ),
            concatMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_user_info, actions)
                    .pipe(
                        map((resp: ICustomResponseResult<Users>) => successUpdatedUserData(resp.data)),
                        catchError((err: Error) => of(errorUpdatedUserData({ payload: err })))
                    )
            )
        )
    );

    /**
     * Realiza el proceso de inicio de sesión del usuario
     * @param payload - Objeto con las credenciales del usuario (teléfono y contraseña) y datos del dispositivo
     * @returns Observable que emite el objeto TenderoLogin con la información del usuario autenticado
     * @description 
     * 1. Adapta el payload al nuevo formato requerido por la API
     * 2. Realiza la petición HTTP de login
     * 3. Decodifica el token JWT recibido
     * 4. Almacena el token JWT en el storage
     * 5. Combina la respuesta con los datos del token decodificado
     */
    public loginTelPass$ = createEffect(
        () => this.actions$.pipe(
            ofType(loginTelPass),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_login, this.mapLoginTelPassToLoginRequest(actions))
                    .pipe(
                        map((response: ICustomResponseResult<Partial<TenderoLogin>>) => this.recompileData(response)),
                        map(resp => successLoginTelPass(resp)),
                        catchError((err: any) => of(errorLoginTelPass({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Realiza el registro del usuario
     */
    public registerUser$ = createEffect(
        () => this.actions$.pipe(
            ofType(registerUser),
            map(actions => ({ ...actions, Telefono: this.encryptionS.encrypt(actions.Telefono), Contrasena: this.encryptionS.encrypt(actions.Contrasena), FechaNacimiento: actions.FechaNacimiento.slice(0, 10) })),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_register, actions)
                    .pipe(
                        map((response: ICustomResponseResult<Partial<ISuccesfull>>) => this.recompileData(response)),
                        map(resp => successRegisterUser(resp)),
                        catchError((err: Error) => of(errorRegisterUser({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Realiza validacion inicial del usuario
     */
    public preRegisterUser$ = createEffect(
        () => this.actions$.pipe(
            ofType(preRegisterUser),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.get_register_validate, actions)
                    .pipe(
                        map(resp => successPreRegisterUser(resp)),
                        catchError((err: Error) => of(errorPreRegisterUser({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Realiza validacion telfono cambio contraseña
     */
    public phoneVaidationPass$ = createEffect(
        () => this.actions$.pipe(
            ofType(phoneVaidationPass),
            switchMap(
                actions => this.httpS.executeRequestByUser(GESTION_USUARIOS_ENDPOINTS.authenticates_phone_retailer, actions, this.httpS.getAzureCAUrl(AZURE_CA_URLS.USUARIOS))
                    .pipe(
                        map(resp => successPhoneVaidationPass(resp[0])),
                        catchError((err: Error) => of(errorPhoneVaidationPass({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Realiza cambio contraseña
     */
    public ChangePass$ = createEffect(
        () => this.actions$.pipe(
            ofType(ChangePass),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.put_update_password, actions)
                    .pipe(
                        map(resp => successChangePass(resp[0])),
                        catchError((err: Error) => of(errorChangePass({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Obtiene los contadores por dispositivo
     */
    public Counters$ = createEffect(
        () => this.actions$.pipe(
            ofType(Counters),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.get_flow_counter, actions)
                    .pipe(
                        map(resp => successCounters(resp)),
                        catchError((err: Error) => of(errorCounters({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Guarda contador por dispositivo
     */
    public CountersSave$ = createEffect(
        () => this.actions$.pipe(
            ofType(CountersSave),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_flow_counter, actions)
                    .pipe(
                        map(resp => successCountersSave(resp)),
                        catchError((err: Error) => of(errorCountersSave({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Envio de verficacion de sms
     */
    public SendSmsPhone$ = createEffect(
        () => this.actions$.pipe(
            ofType(SendSmsPhone),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_create_code, actions)
                    .pipe(
                        map(resp => successSendSmsPhone(resp)),
                        catchError((err: Error) => of(errorSendSmsPhone({ payload: err })))
                    )
            )
        )
    );

    /**
     * * Envio de verficacion de codigo de verificacion
     */
    public SendCodeVerify$ = createEffect(
        () => this.actions$.pipe(
            ofType(SendCodeVerify),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_update_code, actions)
                    .pipe(
                        map(resp => successSendCodeVerify(resp)),
                        catchError((err: Error) => of(errorSendCodeVerify({ payload: err })))
                    )
            )
        )
    );

    /**
     * Refresca el token de autenticación del usuario
     * @param payload - Objeto que contiene los datos necesarios para refrescar el token
     * @returns Observable que emite la respuesta con el nuevo token de autenticación
     * @description
     * Este método realiza una petición HTTP al endpoint de refresh token con los datos proporcionados.
     * La respuesta incluye el nuevo token de autenticación y otros datos relacionados.
     */
    public RefreshAuthToken$ = createEffect(
        () => this.actions$.pipe(
            ofType(RefreshAuthToken),
            switchMap(
                actions => this.httpS.executeRequestByUser(AUTENTIFICACION_ENDPOINTS.post_refresh_auth_token, actions)
                    .pipe(
                        tap((response: ICustomResponseResult<IRefreshAuthTokenResponse>) => {
                            const token = response.data.authToken;
                            if (this.handleErrorS.isSaveToken) StorageData.setDataStorage(AppSettings.JWT_L, token);
                            this.handleErrorS.JWT_L$ = token;
                        }),
                        map((response: ICustomResponseResult<IRefreshAuthTokenResponse>) => successRefreshAuthToken(response.data)),
                        catchError((err: Error) => of(errorRefreshAuthToken({ payload: err })))
                    )
            )
        )
    );

    /**
     * Convierte el formato antiguo de login (ILoginTelPass) al nuevo formato (ILoginRequest)
     * @param payload - Objeto con el formato antiguo de credenciales y datos del dispositivo
     * @returns Objeto adaptado al nuevo formato requerido por la API
     * @description
     * Encripta las credenciales del usuario y estructura los datos adicionales del dispositivo
     */
    private mapLoginTelPassToLoginRequest = (payload: ILoginTelPass): ILoginRequest => ({
        usuario: this.encryptionS.encrypt(payload.Telefono),
        contrasena: this.encryptionS.encrypt(payload.Contrasena),
        datosAdicionales: {
            DeviceID: payload.DeviceID || '',
            Token: payload.Token,
            TokenFirebase: payload.TokenFirebase || '',
            Version: payload.Version,
            DeviceInfo: payload.DeviceInfo,
            offset: payload.offset
        }
    });

    private recompileData = <T>(response: ICustomResponseResult<Partial<T>>) => {
        const token = response.data['authToken'];
        const unwrappedToken = this.utilitiesS.getDecodeTokenJwt(token);
        StorageData.setDataStorage(AppSettings.JWT_L, token)
        return ({ ...response.data, ...unwrappedToken } as any);
    }
}
